import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { createComponentLogger, type ComponentLogger } from '@/lib/logging/frontend-logger';

export interface SectionErrorBoundaryProps {
    readonly sectionName: string;
    readonly children: ReactNode;
    readonly onRetry?: () => void;
}

export interface SectionErrorBoundaryState {
    readonly hasError: boolean;
}

const SectionErrorBoundaryLogger: ComponentLogger = createComponentLogger('SectionErrorBoundary');

export class SectionErrorBoundary extends Component<
    SectionErrorBoundaryProps,
    SectionErrorBoundaryState
> {
    public state: SectionErrorBoundaryState = { hasError: false };

    public static getDerivedStateFromError(): SectionErrorBoundaryState {
        return { hasError: true };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        SectionErrorBoundaryLogger.error('Section render failed', error, {
            section_name: this.props.sectionName,
            component_stack: errorInfo.componentStack ?? '',
            recovery_action: 'local_retry',
        });
    }

    private readonly retry = (): void => {
        this.props.onRetry?.();
        this.setState({ hasError: false });
    };

    public render(): ReactNode {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <Alert variant="destructive">
                <AlertTitle>{this.props.sectionName} could not be displayed.</AlertTitle>
                <AlertDescription>
                    The rest of the page is still available. Try loading this section again.
                </AlertDescription>
                <Button type="button" variant="outline" className="mt-3" onClick={this.retry}>
                    Try again
                </Button>
            </Alert>
        );
    }
}
